/**
 * Add a team: the sheet `/desks/new` opens, in wide mode because the backlog
 * picker needs the room (design notes, "What Bailey sees").
 *
 *   Name       blank keeps the suggested name
 *   Owns       the Team's goal, in a sentence or two
 *   Works on   repository chips, and a search over what the GitHub App covers
 *   Reads      From a filter (with Jira's live answer under it), Only what I
 *              hand it, or Assigned to me (`teams/ReadsChoice.tsx`)
 *   Size       Pull request reviews, or the Full agent team
 *
 * The request each choice makes is `createTeamSize.buildCreateTeamBody`. The
 * sheet renders outside the route's outlet, so the session facts it needs
 * come in as props.
 */
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TEAM_NAME_MAX_LENGTH, type DeskAttachmentInput } from "@yagni/shared";

import { oauthInstallUrl } from "../lib/oauthInstallUrls.js";
import { GitHubInstallOutcome } from "../onboarding/GitHubInstallOutcome.js";
import { readWelcomeFacts } from "../onboarding/useWelcomeRedirect.js";
import { RecordHead, SheetFoot } from "../queue/index.js";
import { SUBSCRIPTIONS_CHANGED_EVENT } from "../shell/useNavDesks.js";
import { captureUiError } from "../sentry.js";
import {
  BacklogAccessNotice,
  backlogAccessMessage,
  readBacklogAccess,
  type BacklogAccessEntry,
} from "../teams/BacklogAccessNotice.js";
import type { BacklogSource } from "../teams/BacklogPreview.js";
import { attachmentForFilterText } from "../teams/backlogFilterText.js";
import { useTrackerOptions } from "../teams/backlogSearch.js";
import { ReadsChoice, type ReadsMode } from "../teams/ReadsChoice.js";
import { attachmentForFilter } from "../teams/settings/settingsDraft.js";
import { TeamWriteError } from "../teams/teamsApi.js";
import { postTeam, trackerLabel, type StartTracker } from "../work/startMyTeamRequest.js";
import type { TrackerIdentity } from "../work/trackerIdentity.js";
import {
  TEAM_SIZES,
  TEAM_SIZE_COPY,
  buildCreateTeamBody,
  createBlockedBy,
  createNotes,
  fullTeamFootCopy,
  readStartsAutonomous,
  repoFacts,
  teamSizeBody,
  teamNameFor,
  type AvailableRepo,
  type TeamSize,
} from "./createTeamSize.js";
import {
  RepoMoreMatches,
  RepoScopeLine,
  RepoSelectAll,
  readRepoScope,
  type RepoScope,
} from "./RepoScopeLine.js";
import "./add-team-sheet.css";

/** How many repositories the search lists before the person narrows it. */
const REPOS_SHOWN = 8;

export interface AddTeamViewer {
  id: string;
  displayName?: string | null;
}

export interface AddTeamSheetProps {
  viewer: AddTeamViewer | null;
  /** Whether the reader may connect GitHub for the workspace. */
  canConnect: boolean;
  initialSize: TeamSize;
  initialReads: ReadsMode;
  /** Cancel, Close, or Escape. */
  onCancel: () => void;
}

interface Person {
  userId: string;
  name: string;
}

interface TrackerCandidate {
  trackerUserId: string;
  displayName: string;
  email: string | null;
}

/**
 * The status of the latest read of a URL when it failed, so a captured
 * failure can say which kind it was. A read that answers clears it.
 */
const failedStatus = new Map<string, number | "network">();

async function readJson<T>(url: string, init?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(url, { credentials: "include", ...init });
    if (res.ok) {
      // A read that answers clears what an earlier failure of the same URL left.
      failedStatus.delete(url);
      return (await res.json()) as T;
    }
    failedStatus.set(url, res.status);
    return null;
  } catch {
    failedStatus.set(url, "network");
    return null;
  }
}

/** "(502)", "(network)", or nothing: what the last failed read of a URL answered. */
function statusOf(url: string): string {
  const status = failedStatus.get(url);
  return status === undefined ? "" : ` (${status})`;
}

/** The workspace's people, for Assigned to me; the viewer always among them. */
function usePeople(viewer: AddTeamViewer | null): Person[] {
  const [members, setMembers] = useState<Person[]>([]);
  useEffect(() => {
    let cancelled = false;
    void readJson<{ members?: unknown }>("/api/members").then((body) => {
      const list = body?.members;
      if (cancelled) return;
      if (!Array.isArray(list)) {
        // Assigned to me still offers the viewer; the rest of the workspace is missing.
        captureUiError(
          new Error(`add team: the members read did not answer${statusOf("/api/members")}`),
          {
            area: "Teams",
            operation: "add_team_read_members",
            severity: "warning",
          },
        );
        return;
      }
      setMembers(
        list
          .filter(
            (entry): entry is { userId: string; displayName?: string; email?: string } =>
              typeof entry?.userId === "string",
          )
          .map((entry) => ({
            userId: entry.userId,
            name: entry.displayName?.trim() || entry.email || entry.userId,
          })),
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return useMemo(() => {
    if (!viewer || members.some((person) => person.userId === viewer.id)) return members;
    return [{ userId: viewer.id, name: viewer.displayName?.trim() || "You" }, ...members];
  }, [members, viewer]);
}

export function AddTeamSheet({
  viewer,
  canConnect,
  initialSize,
  initialReads,
  onCancel,
}: AddTeamSheetProps) {
  const navigate = useNavigate();
  const nameId = useId();
  const ownsId = useId();

  // --- Reads -------------------------------------------------------------
  const [facts, setFacts] = useState<{ hasGitHub: boolean } | null>(null);
  useEffect(() => {
    let cancelled = false;
    void readWelcomeFacts().then((next) => {
      if (!cancelled) setFacts(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const githubConnected = facts?.hasGitHub ?? false;

  const [available, setAvailable] = useState<AvailableRepo[] | null>(null);
  const [repoScope, setRepoScope] = useState<RepoScope | null>(null);
  const [reposFailed, setReposFailed] = useState(false);
  useEffect(() => {
    if (!githubConnected) return;
    let cancelled = false;
    void readJson<{ repos?: AvailableRepo[] }>("/api/repos/available").then((body) => {
      if (cancelled) return;
      if (!body || !Array.isArray(body.repos)) {
        setReposFailed(true);
        setAvailable([]);
        return;
      }
      setAvailable(body.repos);
      setRepoScope(readRepoScope(body));
    });
    return () => {
      cancelled = true;
    };
  }, [githubConnected]);

  // Read, whether or not it answered: a null identity after the read is a
  // failed read, never "no tracker".
  const [identity, setIdentity] = useState<TrackerIdentity | null>(null);
  const [identityRead, setIdentityRead] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void readJson<TrackerIdentity>("/api/tracker/identity").then((body) => {
      if (cancelled) return;
      setIdentity(body);
      setIdentityRead(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // A workspace Managed by YAGNI starts a new Team autonomous; a failed read
  // says supervised, which is what the create falls back to as well.
  const [startsAutonomous, setStartsAutonomous] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void readJson<unknown>("/api/desks/create-defaults").then((body) => {
      if (!cancelled) setStartsAutonomous(readStartsAutonomous(body));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const { options: trackerOptions } = useTrackerOptions(true);
  const people = usePeople(viewer);

  // --- Answers -------------------------------------------------------------
  const [typedName, setTypedName] = useState("");
  const [owns, setOwns] = useState("");
  const [repos, setRepos] = useState<string[]>([]);
  const [size, setSize] = useState<TeamSize>(initialSize);
  const [reads, setReads] = useState<ReadsMode>(initialReads);
  const [filter, setFilter] = useState("");
  const [person, setPerson] = useState<string | null>(viewer?.id ?? null);
  useEffect(() => {
    if (person == null && viewer) setPerson(viewer.id);
  }, [person, viewer]);

  const trackers = useMemo<BacklogSource[]>(() => {
    const out: BacklogSource[] = [];
    if (trackerOptions?.jira.connected) out.push("jira");
    if (trackerOptions?.linear.connected) out.push("linear");
    return out;
  }, [trackerOptions]);
  const [pickedTracker, setPickedTracker] = useState<BacklogSource | null>(null);
  const filterTracker: BacklogSource = pickedTracker ?? trackers[0] ?? "jira";
  // The tracker an Assigned to me or by-hand Team reads: the one the viewer is
  // matched in, else the workspace's first.
  const workspaceTracker: StartTracker | null = identity?.source ?? trackers[0] ?? null;
  const tracker: StartTracker | null = reads === "filter" ? filterTracker : workspaceTracker;

  const personName =
    person && viewer && person === viewer.id
      ? (viewer.displayName ?? null)
      : (people.find((entry) => entry.userId === person)?.name ?? null);
  const suggestedName = teamNameFor({ size, reads, viewerName: personName, repos, owns });
  const name = typedName.trim() || suggestedName;
  const filterReady = attachmentForFilterText(filter, filterTracker) != null;

  // An Assigned to me Team builds only once the tracker read answered: one
  // created while it loads, or after it failed, would be a rail-only Team for
  // someone whose Linear or Jira is connected.
  const identityBlock =
    size === "full" && reads === "me" && !identity
      ? identityRead
        ? "Could not check Linear or Jira. Reload to try again."
        : "Checking Linear and Jira."
      : null;
  const blocked =
    createBlockedBy({ size, reads, name, person, repos, tracker, owns, filterReady }) ??
    identityBlock;

  // --- Create --------------------------------------------------------------
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{
    id: string;
    notes: string[];
    access: BacklogAccessEntry[];
    backlog: DeskAttachmentInput | null;
    imported: unknown;
  } | null>(null);

  const create = useCallback(async () => {
    if (blocked || submitting) return;
    setSubmitting(true);
    setError(null);
    let backlog: DeskAttachmentInput | null = null;
    if (size === "full" && reads === "filter") {
      try {
        backlog = await attachmentForFilter(filter, filterTracker);
      } catch (err) {
        setSubmitting(false);
        // A refusal the person can fix (a link that names nothing) is theirs; anything else is ours.
        if (!(err instanceof TeamWriteError) || err.status >= 500) {
          captureUiError(err, { area: "Teams", operation: "add_team_resolve_filter" });
        }
        setError(
          err instanceof Error
            ? err.message
            : "That filter could not be read. Check it and try again.",
        );
        return;
      }
    }
    const body = buildCreateTeamBody({ size, reads, name, person, repos, tracker, backlog, owns });
    if (!body) {
      setSubmitting(false);
      setError("Something the Team needs is missing. Check the fields above.");
      return;
    }
    const result = await postTeam(body);
    setSubmitting(false);
    if (!result.ok) {
      captureUiError(new Error(`add team create refused: ${result.message}`), {
        area: "Teams",
        operation: "add_team_create",
        severity: "warning",
      });
      setError(result.message);
      return;
    }
    window.dispatchEvent(new Event(SUBSCRIPTIONS_CHANGED_EVENT));
    const notes = createNotes(result.payload);
    // A backlog the Team cannot read yet is something to finish too.
    const access = readBacklogAccess(result.payload).filter(
      (entry) => backlogAccessMessage(entry) !== null,
    );
    // The queue says how the first tickets landed (or did not).
    const imported = (result.payload as { setup?: { imported?: unknown } } | null)?.setup?.imported;
    if (notes.length > 0 || access.length > 0) {
      setCreated({ id: result.id, notes, access, backlog, imported });
      return;
    }
    navigate(
      `/desks/${encodeURIComponent(result.id)}`,
      imported ? { state: { imported } } : undefined,
    );
  }, [
    blocked,
    submitting,
    size,
    reads,
    filter,
    filterTracker,
    name,
    person,
    repos,
    tracker,
    owns,
    navigate,
  ]);

  const head = (
    <RecordHead
      recordKey="NEW TEAM"
      title="Add a team"
      meta={
        <span className="fact" style={{ fontSize: 11.5 }}>
          One Team at any size: reviews only, or the full agent team.
        </span>
      }
      onClose={onCancel}
    />
  );

  if (created) {
    const toFinish = created.notes.length + created.access.length;
    return (
      <>
        {head}
        <div className="sbody addteam" data-testid="add-team-created">
          <p className="body-p" style={{ color: "var(--text-primary)" }}>
            {name} is ready, with {toFinish === 1 ? "one thing" : "a few things"} to finish.
          </p>
          <ul className="addteam-notes">
            {created.notes.map((note) => (
              <li key={note} data-testid="add-team-note">
                {note}
              </li>
            ))}
            {created.access.map((entry) => (
              <li key={`${entry.source}:${entry.ref}`}>
                <BacklogAccessNotice
                  entry={entry}
                  name={
                    created.backlog && created.backlog.ref === entry.ref
                      ? (created.backlog.label ?? null)
                      : null
                  }
                />
              </li>
            ))}
          </ul>
        </div>
        <SheetFoot
          why={
            <>
              The Team exists. <b>Finish these from its settings.</b>
            </>
          }
          primary={{
            label: `Open ${name}`,
            onClick: () =>
              navigate(
                `/desks/${encodeURIComponent(created.id)}`,
                created.imported ? { state: { imported: created.imported } } : undefined,
              ),
          }}
        />
      </>
    );
  }

  return (
    <>
      {head}
      <div className="sbody addteam" data-testid="add-team-sheet">
        <GitHubInstallOutcome returnPath={`/desks/new?size=${size}`} />
        <div className="field">
          <label htmlFor={nameId}>Name</label>
          <div className="in">
            <input
              id={nameId}
              autoFocus
              placeholder={suggestedName}
              value={typedName}
              maxLength={TEAM_NAME_MAX_LENGTH}
              onChange={(event) => setTypedName(event.target.value)}
              data-testid="add-team-name"
            />
          </div>
        </div>
        <div className="field top">
          <label htmlFor={ownsId}>Owns</label>
          <textarea
            id={ownsId}
            rows={2}
            placeholder="What this Team owns, in a sentence or two."
            value={owns}
            onChange={(event) => setOwns(event.target.value)}
            data-testid="add-team-owns"
          />
        </div>
        <div className="field top" data-testid="add-team-repos">
          <label>Works on</label>
          {facts === null ? (
            <span className="fact">Checking GitHub.</span>
          ) : !githubConnected ? (
            <GitHubNeeded size={size} canConnect={canConnect} />
          ) : (
            <RepoPicker
              available={available}
              scope={repoScope}
              failed={reposFailed}
              picked={repos}
              onChange={setRepos}
            />
          )}
        </div>
        <div className="field top" data-testid="add-team-reads">
          <label>Reads</label>
          {size === "reviews" ? (
            <span className="fact" style={{ paddingTop: 8 }}>
              A reviews-only Team reads no tickets. Pick Full agent team below to give it a backlog.
            </span>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
              {reads !== "hand" && trackerOptions && trackers.length === 0 && (
                <span className="fact">
                  Neither Linear nor Jira is connected yet. Connect one in{" "}
                  <Link to="/services" className="ref">
                    Connections
                  </Link>
                  .
                </span>
              )}
              <ReadsChoice
                deskId={null}
                name="reads-new-team"
                mode={reads}
                onModeChange={setReads}
                filter={filter}
                onFilterChange={setFilter}
                tracker={filterTracker}
                trackers={trackers.length > 0 ? trackers : ["jira"]}
                onTrackerChange={setPickedTracker}
                person={person}
                people={people}
                onPersonChange={(userId) => setPerson(userId || null)}
                cap={null}
                foldOnAccept
              />
              {reads === "me" && person != null && viewer != null && person === viewer.id && (
                <IdentityLine identity={identity} onChange={setIdentity} />
              )}
            </div>
          )}
          {size === "full" && reads === "filter" && (
            <div className="help">
              Paste the JQL or the filter link and the tickets appear here before you create
              anything. You can change this later under Bailey.
            </div>
          )}
        </div>
        <div className="field top">
          <label>Size</label>
          <div className="checklist" role="radiogroup" aria-label="Size">
            {TEAM_SIZES.map((entry) => (
              <label key={entry} data-testid={`add-team-size-${entry}`}>
                <input
                  type="radio"
                  name="add-team-size"
                  value={entry}
                  checked={size === entry}
                  onChange={() => setSize(entry)}
                />
                <span>
                  <b style={{ fontWeight: 600 }}>{TEAM_SIZE_COPY[entry].title}</b>
                  <span className="d"> · {teamSizeBody(entry, startsAutonomous)}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        {(error || blocked) && (
          <p
            className={error ? "addteam-error" : "addteam-needs"}
            role={error ? "alert" : "status"}
            data-testid="add-team-needs"
          >
            {error ?? blocked}
          </p>
        )}
      </div>
      <SheetFoot
        why={
          size === "reviews" ? (
            <>
              Proctor reviews the next pull request on these repositories. <b>Nothing else runs.</b>
            </>
          ) : (
            <>
              {fullTeamFootCopy(startsAutonomous).lead}{" "}
              <b>{fullTeamFootCopy(startsAutonomous).bold}</b>
            </>
          )
        }
        secondary={{ label: "Cancel", onClick: onCancel, disabled: submitting }}
        primary={{
          label: submitting ? "Creating" : "Create the Team",
          onClick: () => void create(),
          disabled: blocked != null || submitting,
        }}
      />
    </>
  );
}

function GitHubNeeded({ size, canConnect }: { size: TeamSize; canConnect: boolean }) {
  if (!canConnect) {
    return (
      <span className="fact" style={{ paddingTop: 8 }} data-testid="add-team-github-ask-admin">
        GitHub is not connected yet. An admin connects it for the workspace from{" "}
        <Link to="/services" className="ref">
          Connections
        </Link>
        .
      </span>
    );
  }
  return (
    <span className="fact" style={{ paddingTop: 8 }} data-testid="add-team-github-needed">
      Connect GitHub and pick the repositories this Team works on.{" "}
      <a
        className="ref"
        href={oauthInstallUrl("github", `/desks/new?size=${size}`)}
        data-testid="add-team-connect-github"
      >
        Connect GitHub
      </a>
    </span>
  );
}

function RepoPicker({
  available,
  scope,
  failed,
  picked,
  onChange,
}: {
  available: AvailableRepo[] | null;
  scope: RepoScope | null;
  failed: boolean;
  picked: string[];
  onChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const list = useMemo(() => available ?? [], [available]);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list.filter(
      (repo) => !picked.includes(repo.fullName) && (!q || repo.fullName.toLowerCase().includes(q)),
    );
  }, [list, picked, query]);

  if (available === null)
    return (
      <span className="fact" style={{ paddingTop: 8 }}>
        Reading the repositories GitHub covers.
      </span>
    );
  if (failed) {
    return (
      <span
        className="fact"
        style={{ paddingTop: 8 }}
        role="alert"
        data-testid="add-team-repos-failed"
      >
        GitHub did not list the repositories just now. Reload to try again.
      </span>
    );
  }
  const scopeLine = (
    <RepoScopeLine scope={scope} listed={list.length} testId="add-team-repo-scope" />
  );
  if (list.length === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
        <span className="fact" style={{ paddingTop: 8 }} data-testid="add-team-repos-empty">
          The GitHub App covers no repositories yet. Add some to the installation on GitHub, then
          come back.
        </span>
        {scopeLine}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <div className="tags">
        {picked.map((ref) => (
          <span key={ref} className="chip entry" data-testid={`add-team-chip-${ref}`}>
            {ref}
            <button
              type="button"
              className="x"
              aria-label={`Remove ${ref}`}
              onClick={() => onChange(picked.filter((entry) => entry !== ref))}
            >
              ×
            </button>
          </span>
        ))}
        {!searching && (
          <button
            type="button"
            className="chip add"
            onClick={() => setSearching(true)}
            data-testid="add-team-repo-search-open"
          >
            + search {list.length} {list.length === 1 ? "repository" : "repositories"}
          </button>
        )}
      </div>
      {!searching && scopeLine}
      {searching && (
        <>
          <div className="in">
            <input
              autoFocus
              type="search"
              value={query}
              placeholder="Search repositories"
              aria-label="Search repositories"
              data-testid="add-team-repo-search"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          {scopeLine}
          <div className="checklist" data-testid="add-team-repo-matches">
            <RepoSelectAll
              count={matches.length}
              testId="add-team-repo-select-all"
              onSelect={() => {
                onChange([...picked, ...matches.map((repo) => repo.fullName)]);
                setQuery("");
              }}
            />
            {matches.slice(0, REPOS_SHOWN).map((repo) => (
              <label key={repo.fullName}>
                <input
                  type="checkbox"
                  checked={false}
                  data-testid={`add-team-repo-${repo.fullName}`}
                  onChange={() => {
                    onChange([...picked, repo.fullName]);
                    setQuery("");
                  }}
                />
                <span>
                  {repo.fullName}
                  {repoFacts(repo) && <span className="d"> · {repoFacts(repo)}</span>}
                </span>
              </label>
            ))}
            {matches.length === 0 && (
              <span className="fact" style={{ padding: "2px 8px" }}>
                {query.trim()
                  ? `No repository matches ${query.trim()}.`
                  : "Every repository is picked."}
              </span>
            )}
            <RepoMoreMatches hidden={matches.length - REPOS_SHOWN} />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Who the viewer is in Linear or Jira, for Assigned to me on their own
 * tickets: matched by email, the person they picked, or a pick to make.
 */
function IdentityLine({
  identity,
  onChange,
}: {
  identity: TrackerIdentity | null;
  onChange: (next: TrackerIdentity) => void;
}) {
  const [picking, setPicking] = useState(false);
  const [candidates, setCandidates] = useState<TrackerCandidate[] | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);

  useEffect(() => {
    if (!picking || candidates) return;
    let cancelled = false;
    void readJson<{ users?: TrackerCandidate[]; error?: string | null }>(
      "/api/tracker/identity/candidates",
    ).then((body) => {
      if (cancelled) return;
      setCandidates(Array.isArray(body?.users) ? body.users : []);
      if (!body || body.error)
        setPickError(body?.error ?? "The tracker did not answer. Try again.");
    });
    return () => {
      cancelled = true;
    };
  }, [picking, candidates]);

  async function pin(trackerUserId: string) {
    setPickError(null);
    const next = await readJson<TrackerIdentity>("/api/tracker/identity", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ trackerUserId }),
    });
    if (!next) {
      setPickError("That choice did not save. Try again.");
      captureUiError(new Error(`tracker identity pin failed${statusOf("/api/tracker/identity")}`), {
        area: "Teams",
        operation: "add_team_pin_identity",
        severity: "warning",
      });
      return;
    }
    onChange(next);
    setPicking(false);
  }

  if (!identity) return null;
  if (!identity.source) {
    return (
      <span className="fact" data-testid="add-team-identity-none">
        No Linear or Jira yet, so the Team works from tickets you add on its queue.
      </span>
    );
  }
  const label = trackerLabel(identity.source);
  return (
    <span className="fact" data-testid="add-team-identity">
      {identity.matchedBy === "email" && (
        <>
          {label} knows you as <b>{identity.displayName}</b>, matched by {identity.email}.{" "}
        </>
      )}
      {identity.matchedBy === "pinned" && (
        <>
          {label} knows you as <b>{identity.displayName}</b>, the person you picked.{" "}
        </>
      )}
      {identity.matchedBy === null &&
        (identity.trackerUnavailable ? (
          <>{label} did not answer. Try again in a minute. </>
        ) : (
          <>
            No one in {label} matched {identity.email ?? "your email"}. Pick yourself so the Team
            finds your tickets.{" "}
          </>
        ))}
      {!picking && (
        <button
          type="button"
          className="ref"
          onClick={() => setPicking(true)}
          data-testid="add-team-not-you"
        >
          {identity.matchedBy ? "Not you?" : "Pick yourself"}
        </button>
      )}
      {picking && (
        <select
          aria-label={`You in ${label}`}
          value=""
          style={{ marginLeft: 6 }}
          onChange={(event) => {
            if (event.target.value) void pin(event.target.value);
          }}
          data-testid="add-team-identity-pick"
        >
          <option value="">{candidates ? `Pick yourself in ${label}` : `Reading ${label}`}</option>
          {(candidates ?? []).map((candidate) => (
            <option key={candidate.trackerUserId} value={candidate.trackerUserId}>
              {candidate.displayName}
              {candidate.email ? ` · ${candidate.email}` : ""}
            </option>
          ))}
        </select>
      )}
      {pickError && (
        <span role="alert" style={{ display: "block", color: "var(--incident)" }}>
          {pickError}
        </span>
      )}
    </span>
  );
}
